import React, { useState, useCallback } from 'react';
import axios from 'axios';
import useWebSocket from './hooks/useWebSocket';

import VideoUploader  from './components/VideoUploader';
import VideoPlayer    from './components/VideoPlayer';
import WebcamViewer   from './components/WebcamViewer';
import ModelControls  from './components/ModelControls';
import ReportExporter from './components/ReportExporter';

import { API } from './config';

const DEFAULT_MODEL_CONFIG = {
  safety_belt: { enabled: true, threshold: 0.65 },
  ladder:      { enabled: true, threshold: 0.70 },
  fire:        { enabled: true, threshold: 0.55 },
  fall:        { enabled: true, threshold: 0.85 },
};

export default function App() {
  const [mode, setMode]             = useState('video');
  const [videos, setVideos]         = useState([]);
  const [selectedVideo, setSelected]= useState(null);
  const [currentFrame, setFrame]    = useState(null);
  const [fps, setFps]               = useState(0);
  const [processing, setProcessing] = useState(false);
  const [webcamActive, setWebcam]   = useState(false);
  const [progress, setProgress]     = useState(null);
  const [modelConfig, setModelCfg]  = useState(DEFAULT_MODEL_CONFIG);

  const handleWsMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'frame':
        setFrame(msg.data);
        setFps(msg.fps || 0);
        break;
      case 'progress':
        setProgress(msg);
        break;
      case 'complete':
        setProcessing(false);
        setProgress(null);
        loadVideos();
        break;
      default: break;
    }
  }, []);

  const { connected, sendMessage } = useWebSocket(handleWsMessage);

  const loadVideos = useCallback(async () => {
    const { data } = await axios.get(`${API}/videos`);
    setVideos(data);
  }, []);

  React.useEffect(() => { loadVideos(); }, [loadVideos]);

  const handleProcess = useCallback(async (videoId) => {
    setSelected(videoId);
    setProcessing(true);
    setFrame(null);
    await axios.post(`${API}/videos/${videoId}/process`);
  }, []);

  // Select a completed video for playback without re-processing it
  const handleSelect = useCallback((videoId) => {
    setSelected(videoId);
    setFrame(null);
  }, []);

  const handleModelChange = useCallback(async (config) => {
    setModelCfg(config);
    await axios.post(`${API}/models/config`, config);
  }, []);

  return (
    <div className="min-h-screen bg-surface text-slate-100 flex flex-col">
      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <header className="bg-surface-card border-b border-surface-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center font-bold text-white text-sm">D</div>
          <span className="font-semibold text-slate-200 text-lg">Darwin Intelligence</span>
          <span className="text-slate-500 text-sm">/ Safety Analysis Platform</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${connected ? 'bg-green-900/60 text-green-300' : 'bg-red-900/60 text-red-300'}`}>
            {connected ? '● LIVE' : '○ DISCONNECTED'}
          </span>
          {webcamActive && (
            <span className="text-xs px-2 py-1 rounded-full bg-orange-900/60 text-orange-300 font-medium">
              📹 WEBCAM ACTIVE
            </span>
          )}
        </div>
      </header>

      {/* ── Main layout ──────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── LEFT: Controls ──────────────────────────────────────────────────── */}
        <aside className="w-72 bg-surface-card border-r border-surface-border flex flex-col overflow-y-auto thin-scroll">
          <div className="flex border-b border-surface-border">
            {['video','webcam'].map(m => (
              <button key={m}
                onClick={() => {
                  setMode(m);
                  if (m === 'webcam') { setSelected(null); setFrame(null); }
                }}
                className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors
                  ${mode === m ? 'text-brand border-b-2 border-brand' : 'text-slate-500 hover:text-slate-300'}`}>
                {m === 'video' ? '📁 Video' : '📹 Webcam'}
              </button>
            ))}
          </div>

          <div className="p-4 flex flex-col gap-4 flex-1">
            {mode === 'video'
              ? <VideoUploader videos={videos} onRefresh={loadVideos}
                  onProcess={handleProcess} onSelect={handleSelect}
                  selectedId={selectedVideo}
                  progress={progress} processing={processing} />
              : <WebcamViewer fps={fps} sendMessage={sendMessage} onActiveChange={setWebcam} />
            }

            <div className="border-t border-surface-border pt-4">
              <ModelControls config={modelConfig} onChange={handleModelChange} />
            </div>

            <div className="border-t border-surface-border pt-4 mt-auto">
              <ReportExporter videoId={selectedVideo} videos={videos} />
            </div>
          </div>
        </aside>

        {/* ── CENTER: Player ───────────────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            <VideoPlayer frame={currentFrame} fps={fps} progress={progress}
              videoId={selectedVideo} videos={videos} onRefresh={loadVideos} />
          </div>
        </main>
      </div>
    </div>
  );
}
